import { notify, confirm } from "../../utils/notify";
import React, { useState, useEffect, useRef } from "react";
import * as XLSX from "xlsx";
import Header from "../Header";
import { vendorAPI } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { canAccess } from "../../utils/roles";
import { useListControls, ListToolbar, Pagination } from "../../components/ListControls";
import Topbar from "../../components/Topbar";

const CATEGORIES = [
  "Civil Works",
  "Mechanical",
  "Electrical",
  "Piping",
  "Instrumentation",
  "Structural",
  "Others",
];
const emptyForm = () => ({
  vendorCode: "",
  name: "",
  gst: "",
  pan: "",
  contactPerson: "",
  contactNumber: "",
  email: "",
  address: "",
  bankName: "",
  accountNumber: "",
  ifscCode: "",
  city: "",
  category: "Civil Works",
});

// Columns used for both the import template and the export file.
const IMPORT_COLUMNS = [
  ["name", "Vendor Name"],
  ["vendorCode", "Vendor Code (optional, Admin only)"],
  ["gst", "GST"],
  ["pan", "PAN"],
  ["contactPerson", "Contact Person"],
  ["contactNumber", "Contact Number"],
  ["email", "Email"],
  ["address", "Address"],
  ["bankName", "Bank Name"],
  ["accountNumber", "Account Number"],
  ["ifscCode", "IFSC Code"],
  ["city", "City"],
  ["category", "Category"],
];
const REQUIRED_IMPORT_KEYS = [
  "name",
  "gst",
  "pan",
  "contactPerson",
  "contactNumber",
  "email",
  "address",
  "bankName",
  "accountNumber",
  "ifscCode",
];

function VendorMaster() {
  const { user } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);

  // Bulk import
  const fileInputRef = useRef(null);
  const bulkMenuRef = useRef(null);
  const [bulkMenuOpen, setBulkMenuOpen] = useState(false);
  const [importRows, setImportRows] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);

  const load = () => {
    setLoading(true);
    vendorAPI
      .listVendors()
      .then((d) => setVendors(d.vendors || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!bulkMenuOpen) return;
    const onClickOutside = (e) => {
      if (bulkMenuRef.current && !bulkMenuRef.current.contains(e.target))
        setBulkMenuOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [bulkMenuOpen]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const REQUIRED_LABELS = {
    name: "Vendor name",
    gst: "GST",
    pan: "PAN",
    contactPerson: "Contact person",
    contactNumber: "Contact number",
    email: "Email",
    address: "Address",
    bankName: "Bank name",
    accountNumber: "Account number",
    ifscCode: "IFSC code",
  };

  const handleSave = async () => {
    const missing = Object.keys(REQUIRED_LABELS).filter(
      (k) => !String(form[k] || "").trim(),
    );
    if (missing.length) {
      notify.error(
        `${missing.map((k) => REQUIRED_LABELS[k]).join(", ")} ${missing.length > 1 ? "are" : "is"} required`,
      );
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        await vendorAPI.updateVendor(editId, form);
        notify.success("Vendor updated!");
      } else {
        await vendorAPI.createVendor(form);
        notify.success("Vendor registered! Pending approval.");
      }
      setModal(null);
      setEditId(null);
      setForm(emptyForm());
      load();
    } catch (e) {
      notify.error(e.message || "Failed");
    } finally {
      setSaving(false);
    }
  };

  // ── Bulk Export ────────────────────────────────────────────────────────────
  const handleExport = (fmt) => {
    const rows = filtered.map((v) => ({
      "Vendor Name": v.name,
      "Vendor Code": v.vendorCode || "",
      GST: v.gst || "",
      PAN: v.pan || "",
      "Contact Person": v.contactPerson || "",
      "Contact Number": v.contactNumber || "",
      Email: v.email || "",
      Address: v.address || "",
      "Bank Name": v.bankName || "",
      "Account Number": v.accountNumber || "",
      "IFSC Code": v.ifscCode || "",
      City: v.city || "",
      Category: v.category || "",
      Status: v.status,
      "Approval Status": v.approvalStatus,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Vendors");
    XLSX.writeFile(wb, `vendors-export.${fmt}`, { bookType: fmt });
  };

  const handleDownloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      IMPORT_COLUMNS.map(([, label]) => label),
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Vendor Import Template");
    XLSX.writeFile(wb, "vendor-import-template.xlsx");
  };

  // ── Bulk Import ────────────────────────────────────────────────────────────
  const handleFileSelected = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: "binary" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(sheet, { defval: "" });
        const labelToKey = {};
        IMPORT_COLUMNS.forEach(([key, label]) => {
          labelToKey[label.toLowerCase()] = key;
          labelToKey[key.toLowerCase()] = key;
        });
        const rows = raw
          .map((r) => {
            const row = {};
            Object.keys(r).forEach((col) => {
              const key = labelToKey[col.trim().toLowerCase()] || col.trim();
              row[key] = String(r[col] || "").trim();
            });
            row._missing = REQUIRED_IMPORT_KEYS.filter((k) => !row[k]);
            return row;
          })
          .filter((r) => Object.keys(r).some((k) => k !== "_missing" && r[k]));
        if (!rows.length) {
          notify.error("No data rows found in the file");
          return;
        }
        setImportRows(rows);
        setImportResults(null);
        setModal("IMPORT");
      } catch (err) {
        notify.error("Could not parse file — please use the template format");
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = "";
  };

  const handleConfirmImport = async () => {
    const valid = importRows.filter((r) => r._missing.length === 0);
    if (!valid.length) {
      notify.error(
        "No valid rows to import — fix the missing fields shown in red",
      );
      return;
    }
    setImporting(true);
    try {
      const payload = valid.map(({ _missing, ...rest }) => rest);
      const d = await vendorAPI.bulkImportVendors(payload);
      setImportResults(d);
      notify.success(d.message || "Import complete");
      load();
    } catch (e) {
      notify.error(e.message || "Bulk import failed");
    } finally {
      setImporting(false);
    }
  };

  const lc = useListControls(vendors, {
    searchFields: ["name", "vendorCode", "gst", "pan", "contactPerson"],
    dateField: "createdAt",
    pageSize: 10,
  });
  const filtered = lc.filtered;
  const isAdmin = user?.role === "admin";
  const canManage = canAccess(user, "vendor.master");

  const statusCls = {
    ACTIVE: "status-approved",
    PENDING: "status-pending",
    INACTIVE: "status-rejected",
  };
  const approvalLabel = {
    PENDING_RM: "Pending Reporting Manager",
    PENDING_HOD: "Pending HOD",
    APPROVED: "Approved",
    REJECTED: "Rejected",
  };

  return (
    <>
      <Header />
      <div id="db-wrapper">
        <main id="page-content">
          <Topbar section="Vendor Payment" />
          <section className="container-fluid p-4 pt-3">
            <div className="page-hero d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div>
                <h5 className="text-white lh-1">Vendor Master</h5>
                <div className="sub">
                  Step 1 — Register & manage vendors with GST, PAN, Bank Details
                  and documents
                </div>
              </div>
              <div className="d-flex gap-2 flex-wrap">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  style={{ display: "none" }}
                  onChange={handleFileSelected}
                />
                <div style={{ position: "relative" }} ref={bulkMenuRef}>
                  <button
                    type="button"
                    onClick={() => setBulkMenuOpen((o) => !o)}
                    className="btn fw-semibold px-3 py-2"
                    style={{
                      background: "rgba(255,255,255,0.2)",
                      color: "#fff",
                      borderRadius: 10,
                      border: "1.5px solid rgba(255,255,255,0.35)",
                    }}
                  >
                    <i className="bi bi-arrow-down-up me-2" />
                    Bulk{" "}
                    <i
                      className="bi bi-caret-down-fill ms-1"
                      style={{ fontSize: "0.552rem" }}
                    />
                  </button>
                  {bulkMenuOpen && (
                    <div
                      style={{
                        position: "absolute",
                        right: 0,
                        top: "calc(100% + 6px)",
                        minWidth: 250,
                        background: "#fff",
                        borderRadius: 10,
                        boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
                        border: "1px solid #e5e7eb",
                        zIndex: 20,
                        padding: 4,
                      }}
                    >
                      <button
                        className="dropdown-item"
                        style={{
                          border: "none",
                          background: "none",
                          width: "100%",
                          textAlign: "left",
                          padding: "8px 12px",
                          borderRadius: 6,
                          fontSize: "0.782rem",
                        }}
                        onClick={() => {
                          setBulkMenuOpen(false);
                          fileInputRef.current?.click();
                        }}
                      >
                        <i className="bi bi-upload me-2" />
                        Import Vendors (Excel/CSV)
                      </button>
                      <button
                        className="dropdown-item"
                        style={{
                          border: "none",
                          background: "none",
                          width: "100%",
                          textAlign: "left",
                          padding: "8px 12px",
                          borderRadius: 6,
                          fontSize: "0.782rem",
                        }}
                        onClick={() => {
                          setBulkMenuOpen(false);
                          handleDownloadTemplate();
                        }}
                      >
                        <i className="bi bi-file-earmark-arrow-down me-2" />
                        Download Import Template
                      </button>
                      <hr className="my-1" />
                      <button
                        className="dropdown-item"
                        style={{
                          border: "none",
                          background: "none",
                          width: "100%",
                          textAlign: "left",
                          padding: "8px 12px",
                          borderRadius: 6,
                          fontSize: "0.782rem",
                        }}
                        onClick={() => {
                          setBulkMenuOpen(false);
                          handleExport("xlsx");
                        }}
                      >
                        <i className="bi bi-file-earmark-excel me-2" />
                        Export as Excel
                      </button>
                      <button
                        className="dropdown-item"
                        style={{
                          border: "none",
                          background: "none",
                          width: "100%",
                          textAlign: "left",
                          padding: "8px 12px",
                          borderRadius: 6,
                          fontSize: "0.782rem",
                        }}
                        onClick={() => {
                          setBulkMenuOpen(false);
                          handleExport("csv");
                        }}
                      >
                        <i className="bi bi-file-earmark-text me-2" />
                        Export as CSV
                      </button>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => {
                    setForm(emptyForm());
                    setEditId(null);
                    setModal("ADD");
                  }}
                  className="btn fw-semibold px-4 py-2"
                  style={{
                    background: "rgba(255,255,255,0.2)",
                    color: "#fff",
                    borderRadius: 10,
                    border: "1.5px solid rgba(255,255,255,0.35)",
                  }}
                >
                  <i className="bi bi-plus-circle me-2" />
                  Add Vendor
                </button>
              </div>
            </div>

            <div className="row g-2 mb-3">
              {[
                {
                  label: "Total Vendors",
                  val: vendors.length,
                  bg: "#eff6ff",
                  ic: "#3b82f6",
                  icon: "bi-people",
                },
                {
                  label: "Active",
                  val: vendors.filter((v) => v.status === "ACTIVE").length,
                  bg: "#f0fdf4",
                  ic: "#22c55e",
                  icon: "bi-check-circle",
                },
                {
                  label: "Pending Approval",
                  val: vendors.filter((v) => v.status === "PENDING").length,
                  bg: "#fffbeb",
                  ic: "#f59e0b",
                  icon: "bi-hourglass",
                },
                {
                  label: "Inactive",
                  val: vendors.filter((v) => v.status === "INACTIVE").length,
                  bg: "#fef2f2",
                  ic: "#ef4444",
                  icon: "bi-x-circle",
                },
              ].map((s, i) => (
                <div key={i} className="col-6 col-md-3">
                  <div className="kpi-card d-flex align-items-center gap-2">
                    <div className="kpi-icon" style={{ background: s.bg }}>
                      <i className={`bi ${s.icon}`} style={{ color: s.ic }} />
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: "1.17rem",
                          fontWeight: 700,
                          lineHeight: 1,
                        }}
                      >
                        {s.val}
                      </div>
                      <div
                        style={{
                          fontSize: "0.7rem",
                          color: "#6b7280",
                          marginTop: 2,
                        }}
                      >
                        {s.label}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <ListToolbar
              search={lc.search} setSearch={lc.setSearch}
              dateFrom={lc.dateFrom} setDateFrom={lc.setDateFrom}
              dateTo={lc.dateTo} setDateTo={lc.setDateTo}
              placeholder="Search by name, vendor code, GST, PAN..."
              dateLabel="Registered"
            />

            {loading ? (
              <div className="text-center py-5">
                <span className="spinner-border text-primary" />
              </div>
            ) : lc.paginated.length === 0 ? (
              <div className="empty-state">
                <i className="bi bi-shop" />
                <p>No vendors found</p>
              </div>
            ) : (
              lc.paginated.map((v, idx) => (
                <div key={v._id} className="record-card">
                  <div className="record-number">{(lc.page - 1) * lc.pageSize + idx + 1}.</div>
                  <div className="record-body">
                    <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: "0.874rem",
                          color: "#111827",
                        }}
                      >
                        {v.name}
                      </div>
                      <span
                        className={`status-pill ${statusCls[v.status] || "status-pending"}`}
                      >
                        {v.status}
                      </span>
                      {v.approvalStatus && v.approvalStatus !== "APPROVED" && (
                        <span className="status-pill status-pending">
                          <i className="bi bi-hourglass me-1" />
                          {approvalLabel[v.approvalStatus] || v.approvalStatus}
                        </span>
                      )}
                      <span
                        style={{
                          background: "#f3f4f6",
                          color: "#6b7280",
                          fontSize: "0.7rem",
                          padding: "2px 8px",
                          borderRadius: 12,
                        }}
                      >
                        {v.category}
                      </span>
                    </div>
                    <div className="row g-2">
                      <div className="col-12 col-md-3">
                        <div className="record-field">
                          <span className="rf-label">Vendor Code — </span>
                          <span className="rf-value text-primary">
                            {v.vendorCode ||
                              (v.approvalStatus !== "APPROVED"
                                ? "Pending approval"
                                : "—")}
                          </span>
                        </div>
                        <div className="record-field">
                          <span className="rf-label">GST — </span>
                          <span className="rf-value">{v.gst || "—"}</span>
                        </div>
                        <div className="record-field">
                          <span className="rf-label">PAN — </span>
                          <span className="rf-value">{v.pan}</span>
                        </div>
                      </div>
                      <div className="col-12 col-md-3">
                        <div className="record-field">
                          <span className="rf-label">Bank — </span>
                          <span className="rf-value">{v.bankName || "—"}</span>
                        </div>
                        <div className="record-field">
                          <span className="rf-label">Account — </span>
                          <span className="rf-value">
                            {v.accountNumber || "—"}
                          </span>
                        </div>
                        <div className="record-field">
                          <span className="rf-label">IFSC — </span>
                          <span className="rf-value">{v.ifscCode || "—"}</span>
                        </div>
                      </div>
                      <div className="col-12 col-md-3">
                        <div className="record-field">
                          <span className="rf-label">Contact Person — </span>
                          <span className="rf-value">
                            {v.contactPerson || "—"}
                          </span>
                        </div>
                        <div className="record-field">
                          <span className="rf-label">Contact — </span>
                          <span className="rf-value">
                            {v.contactNumber || "—"}
                          </span>
                        </div>
                        <div className="record-field">
                          <span className="rf-label">Email — </span>
                          <span className="rf-value">{v.email || "—"}</span>
                        </div>
                      </div>
                      <div className="col-12 col-md-3">
                        <div className="record-field">
                          <span className="rf-label">Address — </span>
                          <span className="rf-value">{v.address || "—"}</span>
                        </div>
                        <div className="record-field">
                          <span className="rf-label">City — </span>
                          <span className="rf-value">{v.city || "—"}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {canManage && (
                    <>
                      <div className="divider d-none d-md-block" />
                      <div className="record-actions">
                        <button
                          className="btn-card-view"
                          onClick={() => {
                            setForm({
                              vendorCode: v.vendorCode || "",
                              name: v.name,
                              gst: v.gst || "",
                              pan: v.pan,
                              contactPerson: v.contactPerson || "",
                              contactNumber: v.contactNumber || "",
                              email: v.email || "",
                              address: v.address || "",
                              bankName: v.bankName || "",
                              accountNumber: v.accountNumber || "",
                              ifscCode: v.ifscCode || "",
                              city: v.city || "",
                              category: v.category || "Civil Works",
                            });
                            setEditId(v._id);
                            setModal("EDIT");
                          }}
                        >
                          Edit
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))
            )}

            <Pagination
              page={lc.page} totalPages={lc.totalPages} setPage={lc.setPage}
              totalCount={lc.totalCount} pageSize={lc.pageSize} itemLabel="vendors"
            />

            {(modal === "ADD" || modal === "EDIT") && (
              <div
                className="modal show d-block"
                style={{ background: "rgba(0,0,0,0.45)" }}
              >
                <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                  <div className="modal-content shadow-lg">
                    <div className="modal-header border-0">
                      <h5 className="fw-bold">
                        {modal === "ADD"
                          ? "Register New Vendor"
                          : "Edit Vendor"}
                      </h5>
                      <button
                        className="btn-close"
                        onClick={() => {
                          setModal(null);
                          setEditId(null);
                        }}
                      />
                    </div>
                    <div className="modal-body">
                      {!canManage && modal === "EDIT" && (
                        <div className="alert alert-warning py-2 small">
                          You do not have permission to edit vendor details. Contact admin to get the "Vendor Master" module assigned.
                        </div>
                      )}
                      <div className="row g-2">
                        <div className="col-12">
                          <label className="form-label small fw-semibold">
                            Vendor Name *
                          </label>
                          <input
                            className="form-control"
                            value={form.name}
                            onChange={(e) => set("name", e.target.value)}
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label small fw-semibold">
                            Vendor Code{" "}
                            {isAdmin ? (
                              ""
                            ) : (
                              <span className="text-muted">
                                (auto-generated on approval)
                              </span>
                            )}
                          </label>
                          <input
                            className="form-control"
                            value={form.vendorCode}
                            onChange={(e) => set("vendorCode", e.target.value)}
                            placeholder="VEND-00045"
                            disabled={!isAdmin}
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label small fw-semibold">
                            GST Number *
                          </label>
                          <input
                            className="form-control"
                            value={form.gst}
                            onChange={(e) => set("gst", e.target.value)}
                            placeholder="09AABCA1234B1ZP"
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label small fw-semibold">
                            PAN Number *
                          </label>
                          <input
                            className="form-control"
                            value={form.pan}
                            onChange={(e) => set("pan", e.target.value)}
                            placeholder="AABCA1234B"
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label small fw-semibold">
                            Category
                          </label>
                          <select
                            className="form-select"
                            value={form.category}
                            onChange={(e) => set("category", e.target.value)}
                          >
                            {CATEGORIES.map((c) => (
                              <option key={c}>{c}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-12">
                          <hr className="my-1" />
                          <div className="small fw-bold text-muted">
                            VENDOR CONTACT PERSON
                          </div>
                        </div>
                        <div className="col-md-4">
                          <label className="form-label small fw-semibold">
                            Contact Person Name *
                          </label>
                          <input
                            className="form-control"
                            value={form.contactPerson}
                            onChange={(e) =>
                              set("contactPerson", e.target.value)
                            }
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label small fw-semibold">
                            Contact Phone *
                          </label>
                          <input
                            className="form-control"
                            value={form.contactNumber}
                            onChange={(e) =>
                              set("contactNumber", e.target.value)
                            }
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label small fw-semibold">
                            Contact Email *
                          </label>
                          <input
                            type="email"
                            className="form-control"
                            value={form.email}
                            onChange={(e) => set("email", e.target.value)}
                          />
                        </div>
                        <div className="col-12">
                          <hr className="my-1" />
                          <div className="small fw-bold text-muted">
                            VENDOR ADDRESS
                          </div>
                        </div>
                        <div className="col-md-8">
                          <label className="form-label small fw-semibold">
                            Address *
                          </label>
                          <textarea
                            className="form-control"
                            rows={2}
                            value={form.address}
                            onChange={(e) => set("address", e.target.value)}
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label small fw-semibold">
                            City
                          </label>
                          <input
                            className="form-control"
                            value={form.city}
                            onChange={(e) => set("city", e.target.value)}
                          />
                        </div>
                        <div className="col-12">
                          <hr className="my-1" />
                          <div className="small fw-bold text-muted">
                            BANK DETAILS
                          </div>
                        </div>
                        <div className="col-md-4">
                          <label className="form-label small fw-semibold">
                            Bank Name *
                          </label>
                          <input
                            className="form-control"
                            value={form.bankName}
                            onChange={(e) => set("bankName", e.target.value)}
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label small fw-semibold">
                            Account Number *
                          </label>
                          <input
                            className="form-control"
                            value={form.accountNumber}
                            onChange={(e) =>
                              set("accountNumber", e.target.value)
                            }
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label small fw-semibold">
                            IFSC Code *
                          </label>
                          <input
                            className="form-control"
                            value={form.ifscCode}
                            onChange={(e) => set("ifscCode", e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="modal-footer border-0 gap-2">
                      <button
                        className="btn btn-light rounded-3 px-4"
                        onClick={() => {
                          setModal(null);
                          setEditId(null);
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn rounded-3 px-5 fw-semibold"
                        style={{
                          background: "linear-gradient(135deg,#1a5276,#1e8449)",
                          color: "#fff",
                          border: "none",
                        }}
                        onClick={handleSave}
                        disabled={saving}
                      >
                        {saving ? (
                          <span className="spinner-border spinner-border-sm" />
                        ) : modal === "ADD" ? (
                          "Register Vendor"
                        ) : (
                          "Save Changes"
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {modal === "IMPORT" && (
              <div
                className="modal show d-block"
                style={{ background: "rgba(0,0,0,0.45)" }}
              >
                <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                  <div className="modal-content shadow-lg">
                    <div className="modal-header border-0">
                      <h5 className="fw-bold">
                        <i className="bi bi-upload me-2 text-primary" />
                        Bulk Import Vendors
                      </h5>
                      <button
                        className="btn-close"
                        onClick={() => {
                          setModal(null);
                          setImportRows([]);
                          setImportResults(null);
                        }}
                      />
                    </div>
                    <div className="modal-body">
                      {!importResults ? (
                        <>
                          <div className="mb-2 d-flex align-items-center justify-content-between">
                            <div
                              style={{ fontSize: "0.782rem", color: "#6b7280" }}
                            >
                              {importRows.length} row(s) found —{" "}
                              {
                                importRows.filter(
                                  (r) => r._missing.length === 0,
                                ).length
                              }{" "}
                              valid,{" "}
                              {
                                importRows.filter((r) => r._missing.length > 0)
                                  .length
                              }{" "}
                              with missing fields
                            </div>
                          </div>
                          <div style={{ maxHeight: 360, overflow: "auto" }}>
                            <table className="table table-sm table-bordered">
                              <thead>
                                <tr>
                                  <th>#</th>
                                  <th>Name</th>
                                  <th>GST</th>
                                  <th>PAN</th>
                                  <th>Contact Person</th>
                                  <th>Contact</th>
                                  <th>Email</th>
                                  <th>Bank</th>
                                  <th>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {importRows.map((r, i) => (
                                  <tr
                                    key={i}
                                    style={
                                      r._missing.length
                                        ? { background: "#fef2f2" }
                                        : undefined
                                    }
                                  >
                                    <td>{i + 1}</td>
                                    <td>{r.name || "—"}</td>
                                    <td>{r.gst || "—"}</td>
                                    <td>{r.pan || "—"}</td>
                                    <td>{r.contactPerson || "—"}</td>
                                    <td>{r.contactNumber || "—"}</td>
                                    <td>{r.email || "—"}</td>
                                    <td>{r.bankName || "—"}</td>
                                    <td>
                                      {r._missing.length ? (
                                        <span className="text-danger small">
                                          Missing: {r._missing.join(", ")}
                                        </span>
                                      ) : (
                                        <span className="text-success small">
                                          <i className="bi bi-check-circle me-1" />
                                          Ready
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="alert alert-success py-2 small">
                            {importResults.created?.length || 0} vendor(s)
                            created successfully.
                          </div>
                          {importResults.failed?.length > 0 && (
                            <div className="alert alert-danger py-2 small">
                              {importResults.failed.length} row(s) failed:
                              <ul className="mb-0 mt-1">
                                {importResults.failed.map((f, i) => (
                                  <li key={i}>
                                    Row {f.row} ({f.name}): {f.reason}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    <div className="modal-footer border-0 gap-2">
                      <button
                        className="btn btn-light rounded-3 px-4"
                        onClick={() => {
                          setModal(null);
                          setImportRows([]);
                          setImportResults(null);
                        }}
                      >
                        {importResults ? "Close" : "Cancel"}
                      </button>
                      {!importResults && (
                        <button
                          className="btn rounded-3 px-5 fw-semibold"
                          style={{
                            background:
                              "linear-gradient(135deg,#1a5276,#1e8449)",
                            color: "#fff",
                            border: "none",
                          }}
                          onClick={handleConfirmImport}
                          disabled={importing}
                        >
                          {importing ? (
                            <span className="spinner-border spinner-border-sm" />
                          ) : (
                            <>
                              <i className="bi bi-upload me-1" />
                              Import{" "}
                              {
                                importRows.filter(
                                  (r) => r._missing.length === 0,
                                ).length
                              }{" "}
                              Vendor(s)
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>
        </main>
      </div>
    </>
  );
}
export default VendorMaster;
